import { Stack, Text } from '@mantine/core';
import { FC, useMemo, useState } from 'react';


import { FarmerLayout } from '../../components/farmer-layout/farmer-layout';
import { FieldCapture } from '../../components/field-capture/field-capture';
import { FieldsOverview } from '../../components/fields-overview/fields-overview';
import { GeolocationPrompt } from '../../components/geolocation-prompt/geolocation-prompt';
import { useGeolocation } from '../../hooks/use-geolocation';
import mobileClasses from '../../styles/farmer-mobile.module.css';

import {
  HarvestProofAnswerPublic,
  HarvestProofFormStatus,
  isHarvestProofAnswerSubmitted,
} from '@harvest-proof/data-access';

type FarmerView = 'overview' | 'capture';

interface FarmerHarvestProofPageProps {
  form: {
    account_name: string;
    status: HarvestProofFormStatus;
    answers: HarvestProofAnswerPublic[];
  };
  isSubmitting: boolean;
  onSubmitAnswer: (args: {
    answerId: number;
    photo: File;
    latitude: number;
    longitude: number;
    deviceHeading: number | null;
    clientCapturedAt: string;
  }) => Promise<void>;
  onRefetch: () => void | Promise<void>;
}

export const FarmerHarvestProofPage: FC<FarmerHarvestProofPageProps> = ({
  form,
  isSubmitting,
  onSubmitAnswer,
  onRefetch,
}) => {
  const {
    position,
    geoError,
    startTracking,
    needsPermission,
    isRequesting,
  } = useGeolocation();
  const [view, setView] = useState<FarmerView>('overview');
  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(null);

  const selectedAnswer = useMemo(
    () => form.answers.find((answer) => answer.id === selectedAnswerId) ?? null,
    [form.answers, selectedAnswerId],
  );

  const allFinished = form.answers.every(isHarvestProofAnswerSubmitted);

  const resetCaptureState = () => {
    setSelectedAnswerId(null);
    setView('overview');
  };

  const handleOpenCapture = (answer: HarvestProofAnswerPublic) => {
    if (isHarvestProofAnswerSubmitted(answer)) {
      return;
    }
    setSelectedAnswerId(answer.id);
    setView('capture');
  };

  const handleCaptureAndSubmit = async ({
    file,
    latitude,
    longitude,
    deviceHeading,
  }: {
    file: File;
    latitude: number;
    longitude: number;
    deviceHeading: number | null;
  }) => {
    if (!selectedAnswer) {
      return;
    }

    if (isHarvestProofAnswerSubmitted(selectedAnswer)) {
      resetCaptureState();
      return;
    }

    await onSubmitAnswer({
      answerId: selectedAnswer.id,
      photo: file,
      latitude,
      longitude,
      deviceHeading,
      clientCapturedAt: new Date().toISOString(),
    });

    resetCaptureState();
    await onRefetch();
  };

  if (allFinished) {
    return (
      <FarmerLayout subtitle={form.account_name}>
        <Stack align="center" className={mobileClasses.messageBlock} gap="md">
          <Text fw={700} size="lg" ta="center">
            Merci, toutes les preuves de récolte ont été envoyées.
          </Text>
          <Text c="dimmed" size="md" ta="center">
            Vous pouvez fermer cette page.
          </Text>
        </Stack>
      </FarmerLayout>
    );
  }

  if (form.answers.length === 0) {
    return (
      <FarmerLayout subtitle={form.account_name}>
        <Text className={mobileClasses.messageBlock} size="md">
          Aucune parcelle éligible pour cette campagne.
        </Text>
      </FarmerLayout>
    );
  }

  return (
    <FarmerLayout
      onBack={view === 'capture' ? resetCaptureState : undefined}
      subtitle={form.account_name}
    >
      <GeolocationPrompt
        geoError={needsPermission ? geoError : null}
        isRequesting={isRequesting}
        needsPermission={needsPermission}
        onActivate={startTracking}
      />
      {view === 'overview' ? (
        <FieldsOverview
          answers={form.answers}
          onOpenCapture={handleOpenCapture}
          position={position}
        />
      ) : selectedAnswer ? (
        <FieldCapture
          answer={selectedAnswer}
          isSubmitting={isSubmitting}
          onCaptureAndSubmit={handleCaptureAndSubmit}
          position={position}
        />
      ) : null}
    </FarmerLayout>
  );
};
